import { useState } from "react";
import type { Agreement, PitchView } from "../../api/pitch";
import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { Field, inputShell } from "../../components/Field";
import { Guide } from "../../components/Guide";
import { ScreenHeader } from "../../components/ScreenHeader";
import { numberOrNull } from "../../lib/format";

interface AgreementScreenProps {
  pitch: PitchView;
  busy: boolean;
  message: string | null;
  onChange: (agreement: Agreement) => void;
  onSave: () => void;
}

/** The reusable client agreement, with a live document preview, copy and print. */
export function AgreementScreen({ pitch, busy, message, onChange, onSave }: AgreementScreenProps) {
  const [agreement, setAgreement] = useState<Agreement>(pitch.agreement);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const passes = pitch.agreementChecks.every((check) => check.pass);

  const set = (patch: Partial<Agreement>) => {
    const next = { ...agreement, ...patch };
    setAgreement(next);
    onChange(next);
  };

  const text = (key: keyof Agreement, label: string, placeholder = "") => (
    <Field label={label} htmlFor={`ag-${key}`}>
      <input
        id={`ag-${key}`}
        className={inputShell}
        placeholder={placeholder}
        value={(agreement[key] as string | null) ?? ""}
        onChange={(event) => set({ [key]: event.target.value })}
      />
    </Field>
  );
  const num = (key: "timeline" | "revisions" | "price", label: string) => (
    <Field label={label} htmlFor={`ag-${key}`}>
      <input
        id={`ag-${key}`}
        type="number"
        min={0}
        className={inputShell}
        value={agreement[key] ?? ""}
        onChange={(event) => set({ [key]: numberOrNull(event.target.value) })}
      />
    </Field>
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pitch.agreementText);
      setCopyMessage("Copied.");
    } catch {
      setCopyMessage("Copy didn't work here. Select the text below and copy it.");
    }
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 3 · Pitch · Agree and protect"
        title="Your reusable client agreement."
        sub="Send this before starting any gig. It sets the deliverables, protects your time, and makes sure you get paid. We pre-filled what we could from your niche and rate card."
      />
      <Guide guideKey="agreement" done={pitch.step.agreementDone} />
      {text("yourName", "Your full name")}
      {text(
        "client",
        "Client name (leave blank to keep it as a reusable template)",
        "[Client name]",
      )}
      {text("service", "Service", "e.g. Brand identity design for a coffee shop")}
      <Field label="Deliverables" htmlFor="ag-deliverables">
        <textarea
          id="ag-deliverables"
          rows={2}
          className={inputShell}
          placeholder="Exactly what the client receives"
          value={agreement.deliverables}
          onChange={(event) => set({ deliverables: event.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-3">
        {num("timeline", "Delivery (days)")}
        {num("revisions", "Revision rounds")}
        {num("price", "Fee (USD)")}
      </div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <Field label="Deposit before starting" htmlFor="ag-deposit">
          <select
            id="ag-deposit"
            className={inputShell}
            value={agreement.deposit}
            onChange={(event) => set({ deposit: event.target.value })}
          >
            {["30", "50", "100"].map((d) => (
              <option key={d} value={d}>
                {d}%
              </option>
            ))}
          </select>
        </Field>
        {text("payVia", "How the client pays you", "e.g. Payoneer or bank transfer")}
      </div>
      <Field label="Not included (out of scope)" htmlFor="ag-outScope">
        <textarea
          id="ag-outScope"
          rows={2}
          className={inputShell}
          placeholder="e.g. printing, social media posting, extra concepts"
          value={agreement.outScope}
          onChange={(event) => set({ outScope: event.target.value })}
        />
      </Field>
      <Checks checks={pitch.agreementChecks} />
      <div className="doc my-4 whitespace-pre-wrap rounded-[var(--radius-card)] border border-border bg-surface p-5 text-[13.5px]">
        {pitch.agreementText}
      </div>
      <div className="flex flex-wrap gap-2.5 print:hidden">
        <Button disabled={!passes || busy} onClick={onSave}>
          Save my agreement
        </Button>
        <Button variant="ghost" onClick={() => void copy()}>
          Copy text
        </Button>
        <Button variant="ghost" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
        <BackButton to="/gig/3" label="Back to job board" />
      </div>
      {copyMessage ? <p className="mt-2 text-[13px]">{copyMessage}</p> : null}
      {message ? <Banner tone="good">{message}</Banner> : null}
    </div>
  );
}
